import { forwardRef, useEffect, useMemo } from "react";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import RoundedCheck from "icons/RoundedCheck";
import RoundedInfo from "icons/RoundedInfo";

import {
  StyledAlert,
  StyledAlertToolsContainer,
} from "styles/components/alert";

type AlertType = "error" | "info" | "success" | "warning";

interface AlertProps {
  text: string;
  severity: AlertType;
  withClose?: boolean;
  tools?: JSX.Element;
  variant?: "filled" | "outlined" | "standard";
  dataTestId?: string;
  className?: string;
  autoCloseAfterSeconds?: number;

  onClose?: () => void;
}

const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      text,
      severity,
      withClose,
      tools,
      variant,
      dataTestId,
      className,
      autoCloseAfterSeconds,
      onClose,
    },
    ref
  ) => {
    useEffect(() => {
      if (autoCloseAfterSeconds) {
        const timeout = setTimeout(() => {
          onClose?.();
        }, autoCloseAfterSeconds * 1000);

        return () => clearTimeout(timeout);
      }
    }, [autoCloseAfterSeconds, onClose]);

    const withAction = useMemo(
      () => !!withClose || !!tools,
      [tools, withClose]
    );

    const renderedCloseBtn = useMemo(
      () =>
        withClose ? (
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={onClose}
            data-testid={dataTestId && `${dataTestId}-close-btn`}
          >
            <CloseIcon
              fontSize="inherit"
              data-testid={dataTestId && `${dataTestId}-close-icon`}
            />
          </IconButton>
        ) : null,
      [dataTestId, onClose, withClose]
    );

    const renderedTools = useMemo(() => (tools ? tools : null), [tools]);

    const renderedAction = useMemo(
      () =>
        withAction ? (
          <StyledAlertToolsContainer
            data-testid={dataTestId && `${dataTestId}-action`}
          >
            {renderedTools}
            {renderedCloseBtn}
          </StyledAlertToolsContainer>
        ) : null,
      [dataTestId, renderedCloseBtn, renderedTools, withAction]
    );

    return (
      <StyledAlert
        iconMapping={{
          error: <RoundedInfo />,
          success: <RoundedCheck />,
        }}
        ref={ref}
        severity={severity}
        action={renderedAction}
        variant={variant}
        className={className}
        data-testid={dataTestId}
      >
        {text}
      </StyledAlert>
    );
  }
);

export default Alert;
